/**
 * NOS-133 M14: Event registrations list, CSV, cancel/promote via rsvp-event edge function.
 */

import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { escapeCSVField, downloadCsv } from "@/lib/csv"
import type { Database } from "@/types/database"

type RegistrationRow = Database["public"]["Tables"]["event_registrations"]["Row"]

export interface RegistrationWithTicket extends RegistrationRow {
  event_ticket_types: { id: string; name: string } | null
}

export function useEventRegistrations(eventId: string | null, orgId: string | null) {
  const [registrations, setRegistrations] = useState<RegistrationWithTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchRegistrations = useCallback(async () => {
    if (!eventId || !orgId || eventId === "new") {
      setRegistrations([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: fetchError } = await supabase
      .from("event_registrations")
      .select(
        `
        *,
        event_ticket_types (id, name)
      `
      )
      .eq("event_id", eventId)
      .eq("org_id", orgId)
      .order("registered_at", { ascending: false })

    if (fetchError) {
      setError(fetchError.message)
      setRegistrations([])
    } else {
      setRegistrations((data ?? []) as RegistrationWithTicket[])
    }
    setLoading(false)
  }, [eventId, orgId])

  useEffect(() => {
    fetchRegistrations()
  }, [fetchRegistrations])

  const cancelRegistration = useCallback(
    async (registrationId: string): Promise<boolean> => {
      if (!orgId) return false
      const { error: fnError } = await supabase.functions.invoke("rsvp-event", {
        body: {
          action: "cancel",
          registration_id: registrationId,
          org_id: orgId,
        },
      })
      if (fnError) {
        setError(fnError.message)
        return false
      }
      await fetchRegistrations()
      return true
    },
    [orgId, fetchRegistrations]
  )

  const promoteFromWaitlist = useCallback(
    async (registrationId: string): Promise<boolean> => {
      if (!orgId) return false
      const { error: fnError } = await supabase.functions.invoke("rsvp-event", {
        body: {
          action: "promote",
          registration_id: registrationId,
          org_id: orgId,
        },
      })
      if (fnError) {
        setError(fnError.message)
        return false
      }
      await fetchRegistrations()
      return true
    },
    [orgId, fetchRegistrations]
  )

  const exportCsv = useCallback(
    (rows: RegistrationWithTicket[], eventSlug: string) => {
      const headers = [
        "Name",
        "Email",
        "Ticket type",
        "Headcount",
        "Status",
        "Registered at",
      ]
      const lines = rows.map((r) =>
        [
          escapeCSVField(r.name),
          escapeCSVField(r.email),
          escapeCSVField(r.event_ticket_types?.name ?? ""),
          String(r.headcount),
          escapeCSVField(r.status),
          escapeCSVField(new Date(r.registered_at).toISOString()),
        ].join(",")
      )
      const csv = [headers.join(","), ...lines].join("\n")
      downloadCsv(csv, `${eventSlug}-registrations.csv`)
    },
    []
  )

  return {
    registrations,
    loading,
    error,
    fetchRegistrations,
    cancelRegistration,
    promoteFromWaitlist,
    exportCsv,
  }
}

/** Sum headcount for active statuses per event (for dashboard badges). */
export function useEventRegistrationHeadcountByEvent(orgId: string | null, eventIds: string[]) {
  const [counts, setCounts] = useState<Record<string, number>>({})
  const idKey = eventIds.length ? [...eventIds].sort().join("|") : ""

  useEffect(() => {
    if (!orgId || eventIds.length === 0) {
      setCounts({})
      return
    }
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from("event_registrations")
        .select("event_id, headcount, status")
        .eq("org_id", orgId)
        .in("event_id", eventIds)
        .in("status", ["confirmed", "waitlisted"])
      if (cancelled || !data) return
      const map: Record<string, number> = {}
      for (const row of data) {
        const hc = typeof row.headcount === "number" ? row.headcount : 0
        map[row.event_id] = (map[row.event_id] ?? 0) + hc
      }
      setCounts(map)
    })()
    return () => {
      cancelled = true
    }
  }, [orgId, idKey])

  return counts
}
